import * as THREE from 'three';
import type { App } from '../App';
import type { QualityLevel } from '../core/Quality';
import { PRESET_LABELS, type WeatherPreset } from '../weather/Weather';
import { PLACES, type Place } from './places';

const ICON: Record<string, string> = {
  clear: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="4.2"/><path d="M12 2.5v2.3M12 19.2v2.3M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.3M19.2 12h2.3M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6"/></svg>',
  fair: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M9 5.5V4M4.8 7.3l-1-1M14.2 7.3l1-1"/><path d="M6.2 11.3A3.6 3.6 0 0 1 12.6 9"/><path d="M7.5 19h9.5a3.5 3.5 0 0 0 .4-7 5 5 0 0 0-9.6 1.2A2.9 2.9 0 0 0 7.5 19z"/></svg>',
  overcast: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6.5 18.5h11a4 4 0 0 0 .5-8 6 6 0 0 0-11.4 1.6A3.2 3.2 0 0 0 6.5 18.5z"/></svg>',
  rain: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6.5 14.5h11a4 4 0 0 0 .5-8 6 6 0 0 0-11.4 1.6A3.2 3.2 0 0 0 6.5 14.5z"/><path d="M8 17.5l-1 3M12 17.5l-1 3M16 17.5l-1 3"/></svg>',
  storm: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 14h11a4 4 0 0 0 .5-8 6 6 0 0 0-11.4 1.6A3.2 3.2 0 0 0 6.5 14z"/><path d="M12.5 14.5l-2.5 4h3l-2 3.5"/></svg>',
  snow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9"/><path d="M9.5 4.5L12 6.5l2.5-2M9.5 19.5L12 17.5l2.5 2"/></svg>',
  fog: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 9h11M7 13h13M4 17h12M9 5h8"/></svg>',
  winter: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 19l5.5-9 3.5 5 2.5-3.5L21 19z"/><path d="M7.2 12.2l1.3 1.3 1.3-1.6"/></svg>',
  pin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z"/><circle cx="12" cy="10" r="2.3"/></svg>',
  map: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M3.5 6.5l5.5-2 6 2 5.5-2v13l-5.5 2-6-2-5.5 2z"/><path d="M9 4.5v13M15 6.5v13"/></svg>',
  hike: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="13" cy="4.5" r="1.8"/><path d="M10.5 21l2-6.5 3 2.5V21M12.5 14.5l1-5.5-3 1.5-1.5 3.5M13.5 9l2.5 3h3"/><path d="M18 9.5L19.5 21"/></svg>',
  gear: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z"/></svg>',
  help: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M9.6 9.3a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.2-2.4 3.8"/><path d="M12 17.2h.01"/></svg>',
  full: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
  play: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l10.5-6.5z"/></svg>',
  pause: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z"/></svg>',
  x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PRESETS: WeatherPreset[] = ['clear', 'fair', 'overcast', 'rain', 'storm', 'snow', 'fog', 'winter'];

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', html = ''): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html) e.innerHTML = html;
  return e;
};

function fmtClock(h: number) {
  const hh = Math.floor(h), mm = Math.floor((h - hh) * 60);
  const ampm = hh < 12 ? 'AM' : 'PM';
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
}

export function mountUI(root: HTMLElement, app: App) {
  const isTouch = matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
  if (isTouch) document.body.classList.add('is-touch');
  const store = {
    get(k: string) { try { return localStorage.getItem(`bryce.${k}`); } catch { return null; } },
    set(k: string, v: string) { try { localStorage.setItem(`bryce.${k}`, v); } catch { /* ignore */ } },
  };

  // ---------------- loader
  const loader = el('div', 'loader');
  loader.innerHTML = `<div class="card">
      <h1><small>Bryce Canyon National Park · Utah</small>The Navajo Loop</h1>
      <p class="lede">Walk the switchbacks of Wall Street, look up at Thor’s Hammer, or fly over the Bryce Amphitheater — built from 1 m lidar, aerial imagery and Blender-made hoodoos and trees.</p>
      <div class="bar"><i></i></div>
      <div class="stage">Preparing…</div>
      <div class="go" style="display:none">
        <div class="btn-row"><button class="btn-primary" data-start="walk">Begin at Sunset Point</button><button class="btn-ghost" data-start="fly">Fly over the canyon</button></div>
        <div class="hint">${isTouch ? 'Drag to look · left stick to move' : 'Click to look around · <kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd> move · <kbd>F</kbd> fly · <kbd>?</kbd> help'}</div>
      </div>
      <div class="credits">Elevation: USGS 3DEP lidar · Imagery: USDA NAIP · Trails: © OpenStreetMap contributors</div>
    </div>`;
  root.appendChild(loader);
  const bar = loader.querySelector('.bar > i') as HTMLElement;
  const stage = loader.querySelector('.stage') as HTMLElement;

  // ---------------- HUD
  const hud = el('div', 'hud');
  hud.style.display = 'none';
  root.appendChild(hud);

  const badge = el('div', 'place-badge glass pe');
  badge.innerHTML = `<div class="name">Sunset Point</div><div class="meta"><span class="mode-pill">Walk</span><span><b class="elev">8,010</b> ft</span><span class="coords"></span></div>`;
  hud.appendChild(badge);
  const badgeName = badge.querySelector('.name') as HTMLElement;
  const badgeElev = badge.querySelector('.elev') as HTMLElement;
  const badgeMode = badge.querySelector('.mode-pill') as HTMLElement;
  const badgeCoords = badge.querySelector('.coords') as HTMLElement;

  // compass
  const compass = el('div', 'compass');
  const tape = el('div', 'tape');
  compass.append(tape, el('div', 'center'), el('div', 'hdg'));
  hud.appendChild(compass);
  const PX = 3.2;
  const cardinals: Record<number, string> = { 0: 'N', 45: 'NE', 90: 'E', 135: 'SE', 180: 'S', 225: 'SW', 270: 'W', 315: 'NW' };
  for (let d = -360; d <= 720; d += 5) {
    const t = el('div', 'tick' + (d % 15 === 0 ? ' major' : ''));
    t.style.left = `${(d + 360) * PX}px`;
    tape.appendChild(t);
    const n = ((d % 360) + 360) % 360;
    if (cardinals[n] !== undefined) {
      const l = el('div', 'lbl' + (n % 90 === 0 ? ' card' : ''), cardinals[n]);
      l.style.left = `${(d + 360) * PX}px`;
      tape.appendChild(l);
    }
  }
  const poiLayer = el('div');
  tape.appendChild(poiLayer);
  const hdgEl = compass.querySelector('.hdg') as HTMLElement;

  // toolbar
  const toolbar = el('div', 'toolbar pe');
  const mkBtn = (icon: string, title: string) => {
    const b = el('button', 'icon-btn', ICON[icon]);
    b.title = title;
    b.setAttribute('aria-label', title);
    toolbar.appendChild(b);
    return b;
  };
  const btnPlaces = mkBtn('pin', 'Places (P)');
  const btnMap = mkBtn('map', 'Map (M)');
  const btnHike = mkBtn('hike', 'Guided hike: Navajo Loop (G)');
  const btnSettings = mkBtn('gear', 'Settings');
  const btnHelp = mkBtn('help', 'Controls (?)');
  const btnFull = mkBtn('full', 'Fullscreen');
  hud.appendChild(toolbar);

  // dock
  const dock = el('div', 'dock glass pe');
  dock.innerHTML = `
    <div class="row">
      <button class="icon-btn play" title="Play time (T)" aria-label="Play time" style="width:36px;height:36px">${ICON.play}</button>
      <div class="clock">5:24 PM</div>
      <input class="slider tod" type="range" min="0" max="24" step="0.01" aria-label="Time of day">
      <div class="seg speed" role="group" aria-label="Time speed"><button data-r="0.0167">1×</button><button data-r="0.25" class="on">15m/s</button><button data-r="2">2h/s</button></div>
      <div class="date">Oct 14</div>
    </div>
    <div class="row">
      <div class="seg mode" role="group" aria-label="Movement"><button data-m="walk" class="on">Walk</button><button data-m="fly">Fly</button></div>
      <div class="chips wx"></div>
    </div>`;
  hud.appendChild(dock);
  const tod = dock.querySelector('.tod') as HTMLInputElement;
  const clock = dock.querySelector('.clock') as HTMLElement;
  const dateEl = dock.querySelector('.date') as HTMLElement;
  const playBtn = dock.querySelector('.play') as HTMLButtonElement;
  const wx = dock.querySelector('.wx') as HTMLElement;
  let timeRate = 0.25;
  for (const p of PRESETS) {
    const c = el('button', 'chip', `${ICON[p]}<span>${PRESET_LABELS[p]}</span>`);
    c.dataset.p = p;
    c.addEventListener('click', () => setWeather(p));
    wx.appendChild(c);
  }

  // places drawer
  const places = el('div', 'places glass pe');
  const hikeBtn = el('button', 'btn-primary hike-btn', `${ICON.hike.replace('<svg', '<svg width="18" height="18"')} Hike the Navajo Loop`);
  places.appendChild(hikeBtn);
  for (const g of ['Rim', 'Navajo Loop', 'Aerial'] as const) {
    places.appendChild(el('h3', '', g));
    for (const p of PLACES.filter((q) => q.group === g)) {
      const b = el('button', 'place', `<b>${p.name}</b><span>${p.note}</span>`);
      b.addEventListener('click', () => goTo(p));
      places.appendChild(b);
    }
  }
  hud.appendChild(places);

  // minimap
  const mm = el('div', 'minimap glass pe');
  const mmCanvas = el('canvas');
  mmCanvas.width = mmCanvas.height = 640;
  const mmLabel = el('div', 'mm-label', 'Click to travel · scroll to zoom');
  mm.append(mmCanvas, mmLabel);
  hud.appendChild(mm);
  let mmScale = 900; // meters across

  // modals
  const settings = el('div', 'modal');
  settings.innerHTML = `<div class="sheet glass">
    <button class="icon-btn close" aria-label="Close">${ICON.x}</button>
    <h2>Settings</h2>
    <div class="setting"><span>Graphics quality</span><div class="seg q">${(['low', 'medium', 'high', 'ultra'] as QualityLevel[]).map((q) => `<button data-q="${q}">${q[0].toUpperCase() + q.slice(1)}</button>`).join('')}</div><span></span></div>
    <div class="setting"><span>Look</span><div class="seg look"><button data-t="1">Vivid</button><button data-t="0">Natural</button></div><span></span></div>
    <div class="setting"><span>Exposure</span><input class="slider plain exp" type="range" min="-2" max="2" step="0.1" value="0"><output class="o-exp">0.0</output></div>
    <div class="setting"><span>Field of view</span><input class="slider plain fov" type="range" min="45" max="100" step="1" value="70"><output class="o-fov">70°</output></div>
    <div class="setting"><span>Look sensitivity</span><input class="slider plain sens" type="range" min="0.4" max="2.5" step="0.05" value="1"><output class="o-sens">1.00</output></div>
    <div class="setting"><span>Invert Y</span><div class="seg inv"><button data-v="0" class="on">Off</button><button data-v="1">On</button></div><span></span></div>
    <div class="setting"><span>Sound</span><input class="slider plain vol" type="range" min="0" max="1" step="0.01" value="0.7"><output class="o-vol">70%</output></div>
    <div class="setting"><span>Date</span><input class="date-in" type="date" style="background:rgba(255,255,255,0.08);color:inherit;border:1px solid var(--line);border-radius:8px;padding:6px 8px"><span></span></div>
  </div>`;
  root.appendChild(settings);
  const help = el('div', 'modal');
  help.innerHTML = `<div class="sheet glass">
    <button class="icon-btn close" aria-label="Close">${ICON.x}</button>
    <h2>Controls</h2>
    <div class="keys">
      <div><kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd></div><div>Walk / fly</div>
      <div>Mouse / drag</div><div>Look around (click the view to capture the mouse, <kbd>Esc</kbd> to release)</div>
      <div><kbd>Shift</kbd></div><div>Run (walk) · boost ×4 (fly)</div>
      <div><kbd>Space</kbd></div><div>Jump (walk) · up (fly)</div>
      <div><kbd>C</kbd> / <kbd>Q</kbd> · <kbd>E</kbd></div><div>Down · up (fly)</div>
      <div>Wheel</div><div>Flight speed</div>
      <div><kbd>F</kbd></div><div>Toggle walk / fly</div>
      <div><kbd>G</kbd></div><div>Guided hike along the Navajo Loop</div>
      <div><kbd>T</kbd> · <kbd>[</kbd> <kbd>]</kbd></div><div>Play time · step ±30 min</div>
      <div><kbd>1</kbd>–<kbd>8</kbd></div><div>Weather presets</div>
      <div><kbd>P</kbd> · <kbd>M</kbd></div><div>Places · map</div>
      <div><kbd>H</kbd></div><div>Hide interface (photo mode)</div>
    </div>
    <p style="font-size:12px;color:var(--muted);margin:16px 0 0;line-height:1.5">Terrain is real: USGS 3DEP 1 m lidar, USDA NAIP imagery, OpenStreetMap trails. Hoodoos are placed where the lidar shows spires and fins; the sun and moon follow the real sky over Bryce for the chosen date.</p>
  </div>`;
  root.appendChild(help);
  for (const m of [settings, help]) {
    m.addEventListener('click', (e) => { if (e.target === m || (e.target as HTMLElement).closest('.close')) m.classList.remove('open'); });
  }

  const toast = el('div', 'toast glass');
  hud.appendChild(toast);
  const crosshair = el('div', 'crosshair');
  hud.appendChild(crosshair);
  const lockHint = el('div', 'lock-hint', '<kbd>Esc</kbd> free the cursor to use the controls');
  hud.appendChild(lockHint);
  hud.appendChild(el('div', 'attribution', 'USGS 3DEP · USDA NAIP · © OpenStreetMap contributors'));

  // touch controls
  const touch = el('div', 'touch');
  touch.innerHTML = `<div class="stick pe"><i></i></div><div class="tbtns pe"><button class="tbtn up">Up</button><button class="tbtn down">Down</button></div>`;
  hud.appendChild(touch);

  // ---------------- behaviour
  let toastTimer = 0;
  const showToast = (msg: string) => {
    toast.textContent = msg;
    toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('show'), 1800);
  };

  const setMode = (m: 'walk' | 'fly', quiet = false) => {
    app.hike?.stop();
    app.controller.setMode(m);
    dock.querySelectorAll<HTMLButtonElement>('.mode button').forEach((b) => b.classList.toggle('on', b.dataset.m === m));
    if (!quiet) showToast(m === 'fly' ? 'Flying — wheel changes speed, Space/E up, C/Q down' : 'Walking');
  };
  dock.querySelectorAll<HTMLButtonElement>('.mode button').forEach((b) => b.addEventListener('click', () => setMode(b.dataset.m as 'walk' | 'fly')));

  const setWeather = (p: WeatherPreset) => {
    app.weather.set(p);
    wx.querySelectorAll<HTMLButtonElement>('.chip').forEach((c) => c.classList.toggle('on', c.dataset.p === p));
    showToast(PRESET_LABELS[p]);
    store.set('weather', p);
  };

  const setPlaying = (on: boolean) => {
    app.time.rate = on ? timeRate : 0;
    playBtn.innerHTML = on ? ICON.pause : ICON.play;
    playBtn.classList.toggle('on', on);
  };
  playBtn.addEventListener('click', () => setPlaying(app.time.rate === 0));
  dock.querySelectorAll<HTMLButtonElement>('.speed button').forEach((b) => b.addEventListener('click', () => {
    timeRate = +b.dataset.r!;
    dock.querySelectorAll('.speed button').forEach((x) => x.classList.toggle('on', x === b));
    if (app.time.rate !== 0) app.time.rate = timeRate;
  }));
  tod.addEventListener('input', () => { app.time.hour = +tod.value; app.pipeline.snapExposure(); });

  const goTo = (p: Place) => {
    app.hike?.stop();
    setMode(p.mode, true);
    app.controller.teleport(p.x, p.z, p.heading, p.pitch, p.mode === 'fly' ? p.height : undefined);
    app.pipeline.snapExposure();
    places.classList.remove('open');
    btnPlaces.classList.remove('on');
    showToast(p.name);
  };

  const toggle = (panel: HTMLElement, btn: HTMLElement) => {
    const open = !panel.classList.contains('open');
    panel.classList.toggle('open', open);
    btn.classList.toggle('on', open);
  };
  btnPlaces.addEventListener('click', () => toggle(places, btnPlaces));
  btnMap.addEventListener('click', () => toggle(mm, btnMap));
  const startHike = () => {
    if (!app.hike) return;
    if (app.hike.active) { app.hike.stop(); showToast('Guided hike stopped'); return; }
    setMode('walk', true);
    app.hike.start();
    places.classList.remove('open');
    showToast('Hiking the Navajo Loop — look around freely, move to take over');
  };
  btnHike.addEventListener('click', startHike);
  hikeBtn.addEventListener('click', startHike);
  btnSettings.addEventListener('click', () => settings.classList.add('open'));
  btnHelp.addEventListener('click', () => help.classList.add('open'));
  btnFull.addEventListener('click', () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen?.().catch(() => undefined);
  });

  // settings wiring
  const q = settings.querySelector('.q') as HTMLElement;
  const markQ = () => q.querySelectorAll<HTMLButtonElement>('button').forEach((b) => b.classList.toggle('on', b.dataset.q === app.quality.level));
  q.querySelectorAll<HTMLButtonElement>('button').forEach((b) => b.addEventListener('click', () => {
    app.setQuality(b.dataset.q as QualityLevel);
    store.set('quality', b.dataset.q!);
    markQ();
    showToast(`Quality: ${b.textContent}`);
  }));
  const look = settings.querySelector('.look') as HTMLElement;
  const setLook = (t: number) => {
    app.pipeline.finalUniforms.uTonemap.value = t;
    look.querySelectorAll<HTMLButtonElement>('button').forEach((b) => b.classList.toggle('on', +b.dataset.t! === t));
    store.set('look', String(t));
  };
  look.querySelectorAll<HTMLButtonElement>('button').forEach((b) => b.addEventListener('click', () => setLook(+b.dataset.t!)));
  const bindRange = (cls: string, out: string, fmt: (v: number) => string, apply: (v: number) => void, key: string) => {
    const inp = settings.querySelector(cls) as HTMLInputElement;
    const o = settings.querySelector(out) as HTMLElement;
    const saved = store.get(key);
    if (saved !== null) inp.value = saved;
    const f = () => { apply(+inp.value); o.textContent = fmt(+inp.value); store.set(key, inp.value); };
    inp.addEventListener('input', f);
    f();
  };
  const inv = settings.querySelector('.inv') as HTMLElement;
  inv.querySelectorAll<HTMLButtonElement>('button').forEach((b) => b.addEventListener('click', () => {
    app.controller.invertY = b.dataset.v === '1';
    inv.querySelectorAll('button').forEach((x) => x.classList.toggle('on', x === b));
  }));
  const dateIn = settings.querySelector('.date-in') as HTMLInputElement;
  dateIn.addEventListener('change', () => {
    const [y, m, d] = dateIn.value.split('-').map(Number);
    if (y && m && d) { app.time.year = y; app.time.month = m - 1; app.time.day = d; }
  });

  // keyboard shortcuts
  window.addEventListener('keydown', (e) => {
    if ((e.target as HTMLElement).tagName === 'INPUT') return;
    if (loader.parentElement && !loader.classList.contains('hide')) return;
    const k = e.code;
    if (k === 'KeyF') setMode(app.controller.mode === 'walk' ? 'fly' : 'walk');
    else if (k === 'KeyM') toggle(mm, btnMap);
    else if (k === 'KeyP') toggle(places, btnPlaces);
    else if (k === 'KeyG') startHike();
    else if (k === 'KeyH') hud.classList.toggle('hidden');
    else if (k === 'KeyT') setPlaying(app.time.rate === 0);
    else if (k === 'BracketLeft') { app.time.hour = (app.time.hour + 23.5) % 24; app.pipeline.snapExposure(); }
    else if (k === 'BracketRight') { app.time.hour = (app.time.hour + 0.5) % 24; app.pipeline.snapExposure(); }
    else if (/^Digit[1-8]$/.test(k)) setWeather(PRESETS[+k.slice(5) - 1]);
    else if (e.key === '?' || k === 'Slash') help.classList.toggle('open');
    else if (k === 'Escape') { settings.classList.remove('open'); help.classList.remove('open'); }
  });

  // touch stick
  const stick = touch.querySelector('.stick') as HTMLElement;
  const knob = stick.querySelector('i') as HTMLElement;
  let stickId: number | null = null;
  const stickMove = (e: PointerEvent) => {
    const r = stick.getBoundingClientRect();
    let dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
    let dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
    const l = Math.hypot(dx, dy);
    if (l > 1) { dx /= l; dy /= l; }
    app.input.stick.x = dx;
    app.input.stick.y = -dy;
    knob.style.transform = `translate(${dx * 36}px, ${dy * 36}px)`;
  };
  stick.addEventListener('pointerdown', (e) => { stickId = e.pointerId; stick.setPointerCapture(e.pointerId); stickMove(e); e.stopPropagation(); });
  stick.addEventListener('pointermove', (e) => { if (e.pointerId === stickId) stickMove(e); });
  const stickEnd = (e: PointerEvent) => {
    if (e.pointerId !== stickId) return;
    stickId = null; app.input.stick.x = app.input.stick.y = 0; knob.style.transform = '';
  };
  stick.addEventListener('pointerup', stickEnd);
  stick.addEventListener('pointercancel', stickEnd);
  stick.addEventListener('lostpointercapture', stickEnd);
  const hold = (sel: string, on: (v: boolean) => void) => {
    const b = touch.querySelector(sel) as HTMLElement;
    b.addEventListener('pointerdown', (e) => { on(true); b.setPointerCapture(e.pointerId); });
    for (const ev of ['pointerup', 'pointercancel', 'lostpointercapture']) b.addEventListener(ev, () => on(false));
  };
  hold('.up', (v) => { app.input.touchUp = v; if (v && app.controller.mode === 'walk') app.input.keys.add('Space'); else app.input.keys.delete('Space'); });
  hold('.down', (v) => { app.input.touchDown = v; });

  // minimap
  const mmCtx = mmCanvas.getContext('2d')!;
  let mmImage: CanvasImageSource | null = null;
  mm.addEventListener('wheel', (e) => { e.preventDefault(); mmScale = THREE.MathUtils.clamp(mmScale * (e.deltaY > 0 ? 1.25 : 0.8), 250, 3000); }, { passive: false });
  mmCanvas.addEventListener('click', (e) => {
    const r = mmCanvas.getBoundingClientRect();
    const u = (e.clientX - r.left) / r.width - 0.5, v = (e.clientY - r.top) / r.height - 0.5;
    const p = app.controller.position;
    const x = p.x + u * mmScale, z = p.z + v * mmScale;
    app.hike?.stop();
    if (app.controller.mode === 'fly') app.controller.teleport(x, z, THREE.MathUtils.radToDeg(app.controller.yaw), THREE.MathUtils.radToDeg(app.controller.pitch), Math.max(60, app.controller.heightAboveGround));
    else app.controller.teleport(x, z, THREE.MathUtils.radToDeg(app.controller.yaw), 0);
    app.pipeline.snapExposure();
  });
  const drawMinimap = () => {
    if (!mm.classList.contains('open')) return;
    const L = app.terrainData.near;
    if (!mmImage) mmImage = (L.albedoTex.image as CanvasImageSource) ?? null;
    const W = mmCanvas.width;
    const p = app.controller.position;
    const s = W / mmScale; // px per meter
    mmCtx.fillStyle = '#2b1a12';
    mmCtx.fillRect(0, 0, W, W);
    if (mmImage) {
      const ps = L.size / L.res;
      const sx = (p.x - mmScale / 2 - L.x0) / ps, sy = (p.z - mmScale / 2 - L.z0) / ps;
      mmCtx.drawImage(mmImage, sx, sy, mmScale / ps, mmScale / ps, 0, 0, W, W);
    }
    const toPx = (x: number, z: number): [number, number] => [(x - p.x) * s + W / 2, (z - p.z) * s + W / 2];
    mmCtx.lineCap = 'round';
    mmCtx.lineJoin = 'round';
    for (const t of app.osm.trails) {
      if (t.informal) continue;
      const navajo = /Navajo/.test(t.name);
      mmCtx.strokeStyle = navajo ? '#ffb07a' : t.paved ? 'rgba(255,255,255,0.75)' : 'rgba(255,236,214,0.8)';
      mmCtx.lineWidth = navajo ? 3.2 : 1.8;
      mmCtx.beginPath();
      t.pts.forEach(([x, z], i) => { const [a, b] = toPx(x, z); if (i) mmCtx.lineTo(a, b); else mmCtx.moveTo(a, b); });
      mmCtx.stroke();
    }
    mmCtx.font = '600 15px Inter, sans-serif';
    mmCtx.textAlign = 'center';
    for (const poi of app.osm.pois) {
      const [a, b] = toPx(poi.x, poi.z);
      if (a < -40 || b < -20 || a > W + 40 || b > W + 20) continue;
      mmCtx.fillStyle = '#f0894a';
      mmCtx.beginPath(); mmCtx.arc(a, b, 4.5, 0, Math.PI * 2); mmCtx.fill();
      mmCtx.fillStyle = '#fff6ec';
      mmCtx.strokeStyle = 'rgba(0,0,0,0.65)';
      mmCtx.lineWidth = 3;
      mmCtx.strokeText(poi.name, a, b - 9);
      mmCtx.fillText(poi.name, a, b - 9);
    }
    // player arrow
    mmCtx.save();
    mmCtx.translate(W / 2, W / 2);
    mmCtx.rotate(app.controller.yaw);
    mmCtx.fillStyle = '#ffffff';
    mmCtx.strokeStyle = '#e9743a';
    mmCtx.lineWidth = 3;
    mmCtx.beginPath(); mmCtx.moveTo(0, -14); mmCtx.lineTo(9, 10); mmCtx.lineTo(0, 5); mmCtx.lineTo(-9, 10); mmCtx.closePath();
    mmCtx.fill(); mmCtx.stroke();
    mmCtx.restore();
    // scale bar
    mmCtx.fillStyle = 'rgba(255,246,236,0.9)';
    const barM = mmScale > 1500 ? 500 : mmScale > 600 ? 200 : 100;
    mmCtx.fillRect(W - 24 - barM * s, W - 22, barM * s, 3);
    mmCtx.font = '500 13px Inter, sans-serif';
    mmCtx.textAlign = 'right';
    mmCtx.fillText(`${barM} m`, W - 24, W - 30);
  };

  // nearest named place
  const named = () => {
    const out: { name: string; x: number; z: number }[] = [];
    for (const p of PLACES) out.push({ name: p.name, x: p.x, z: p.z });
    for (const p of app.osm.pois) out.push(p);
    return out;
  };
  let namedCache: { name: string; x: number; z: number }[] | null = null;
  const placeName = () => {
    namedCache ??= named();
    const p = app.controller.position;
    const agl = app.controller.heightAboveGround;
    if (app.controller.mode === 'fly' && agl > 400) return agl > 1500 ? 'Above the Paunsaugunt Plateau' : 'Over Bryce Canyon';
    let best = '', bd = Infinity;
    for (const q of namedCache) {
      const d = Math.hypot(q.x - p.x, q.z - p.z);
      if (d < bd) { bd = d; best = q.name; }
    }
    if (bd < 120) return best;
    if (app.hike?.active) return 'Navajo Loop Trail';
    const g = app.terrainData.heightAt(p.x, p.z);
    if (g > 2420) return 'Paunsaugunt Plateau rim';
    if (p.x > -200 && p.x < 2200 && p.z > -1200 && p.z < 2200) return bd < 400 ? `Near ${best}` : 'Bryce Amphitheater';
    return 'Bryce Canyon National Park';
  };

  // compass POIs
  const COMPASS_POIS = ["Thor's Hammer", 'Sunrise Point', 'Sunset Point', 'Inspiration Point', 'Bryce Point', 'Boat Mesa', 'Two Bridges', 'Queen Victoria'];
  const FAR: { name: string; x: number; z: number }[] = [
    { name: 'Navajo Mtn', x: 114000, z: 78000 },
    { name: 'Aquarius Plateau', x: 42000, z: -30000 },
    { name: 'Powell Point', x: 26500, z: -11000 },
  ];
  let poiTimer = 0;
  const refreshCompassPois = () => {
    poiLayer.innerHTML = '';
    const p = app.controller.position;
    const list = [...app.osm.pois.filter((q) => COMPASS_POIS.includes(q.name)), ...FAR];
    const seen = new Set<string>();
    const cand: { name: string; brg: number; d: number }[] = [];
    for (const q of list) {
      if (seen.has(q.name)) continue;
      seen.add(q.name);
      const d = Math.hypot(q.x - p.x, q.z - p.z);
      if (d < 25 || d > 160000 || (d > 4000 && !FAR.includes(q))) continue;
      cand.push({ name: q.name, brg: (THREE.MathUtils.radToDeg(Math.atan2(q.x - p.x, -(q.z - p.z))) + 360) % 360, d });
    }
    // nearest first; skip labels that would overlap an already placed one
    cand.sort((a, b) => a.d - b.d);
    const placed: number[] = [];
    for (const c of cand) {
      const minSep = (c.name.length * 6.2 + 14) / PX;
      if (placed.some((b) => Math.abs(((c.brg - b + 540) % 360) - 180) < minSep)) continue;
      placed.push(c.brg);
      for (const off of [-360, 0, 360]) {
        const e = el('div', 'poi', c.name);
        e.style.left = `${(c.brg + off + 360) * PX}px`;
        poiLayer.appendChild(e);
      }
    }
  };

  let hudTick = 0;
  const update = (dt: number) => {
    // compass
    const yawDeg = ((THREE.MathUtils.radToDeg(app.controller.yaw) % 360) + 360) % 360;
    const w = compass.clientWidth;
    tape.style.transform = `translateX(${w / 2 - (yawDeg + 360) * PX}px)`;
    hdgEl.textContent = `${Math.round(yawDeg)}°`;
    poiTimer -= dt;
    if (poiTimer <= 0) { poiTimer = 0.6; refreshCompassPois(); }
    crosshair.classList.toggle('show', app.input.pointerLocked);
    lockHint.classList.toggle('show', app.input.pointerLocked);
    hudTick -= dt;
    if (hudTick > 0) return;
    hudTick = 0.2;
    const p = app.controller.position;
    const ft = (p.y - (app.controller.mode === 'walk' ? app.controller.eyeHeight : 0)) * 3.28084;
    badgeElev.textContent = Math.round(ft).toLocaleString('en-US');
    badgeName.textContent = placeName();
    badgeMode.textContent = app.hike?.active ? 'Hiking' : app.controller.mode === 'fly' ? `Fly · ${Math.round(app.controller.flySpeed)} m/s` : 'Walk';
    badgeCoords.textContent = app.controller.mode === 'fly' ? `${Math.round(app.controller.heightAboveGround)} m above ground` : '';
    if (document.activeElement !== tod) tod.value = String(app.time.hour);
    clock.textContent = fmtClock(app.time.hour);
    dateEl.textContent = `${MONTHS[app.time.month]} ${app.time.day}`;
    drawMinimap();
  };

  return {
    setProgress(f: number, label: string) {
      bar.style.width = `${Math.round(f * 100)}%`;
      stage.textContent = `${label[0].toUpperCase()}${label.slice(1)} · ${Math.round(f * 100)}%`;
    },
    onLoaded() {
      stage.textContent = 'Ready';
      bar.style.width = '100%';
      (loader.querySelector('.go') as HTMLElement).style.display = '';
      (loader.querySelector('.bar') as HTMLElement).style.display = 'none';
      const begin = (mode: 'walk' | 'fly') => {
        loader.classList.add('hide');
        setTimeout(() => loader.remove(), 1000);
        hud.style.display = '';
        app.audio?.unlock();
        if (mode === 'fly') goTo(PLACES.find((p) => p.id === 'aerial')!);
        if (!isTouch) Promise.resolve(app.canvas.requestPointerLock?.()).catch(() => undefined);
      };
      loader.querySelectorAll<HTMLButtonElement>('[data-start]').forEach((b) => b.addEventListener('click', () => begin(b.dataset.start as 'walk' | 'fly')));
      // restore settings
      const wp = (store.get('weather') as WeatherPreset | null) ?? 'clear';
      app.weather.set(PRESETS.includes(wp) ? wp : 'clear', true);
      wx.querySelectorAll<HTMLButtonElement>('.chip').forEach((c) => c.classList.toggle('on', c.dataset.p === app.weather.preset));
      setLook(+(store.get('look') ?? '1'));
      markQ();
      dateIn.value = `${app.time.year}-${String(app.time.month + 1).padStart(2, '0')}-${String(app.time.day).padStart(2, '0')}`;
      bindRange('.exp', '.o-exp', (v) => (v > 0 ? '+' : '') + v.toFixed(1), (v) => { app.pipeline.exposureBias = v; }, 'exp');
      bindRange('.fov', '.o-fov', (v) => `${v}°`, (v) => { app.camera.fov = v; app.camera.updateProjectionMatrix(); }, 'fov');
      bindRange('.sens', '.o-sens', (v) => v.toFixed(2), (v) => { app.controller.sensitivity = 0.0022 * v; }, 'sens');
      bindRange('.vol', '.o-vol', (v) => `${Math.round(v * 100)}%`, (v) => app.audio?.setVolume(v), 'vol');
      app.onFrame = update;
      // auto-start for automation (?autostart)
      if (new URLSearchParams(location.search).has('autostart')) begin('walk');
    },
    showError(msg: string) {
      stage.textContent = `Could not load: ${msg}`;
      stage.style.color = '#ffb4a0';
    },
  };
}
